import React, { useMemo } from "react";
import SectionTitle from "../components/SectionTitle";
import Seo from "../components/Seo";
import { MapPin, Info, Clock } from "lucide-react";
import { useData } from "../context/DataContext";

const DAY_LABELS = {
  monday: "Lundi", tuesday: "Mardi", wednesday: "Mercredi",
  thursday: "Jeudi", friday: "Vendredi", saturday: "Samedi", sunday: "Dimanche",
};
const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const fmtMinutes = (mins) => {
  if (!mins) return "";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h${String(m).padStart(2, "0")}`;
  if (h) return `${h}h`;
  return `${m}min`;
};

const minutesBetween = (start, end) => {
  try {
    const [sh, sm] = (start || "").split(":").map(Number);
    const [eh, em] = (end || "").split(":").map(Number);
    return Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
  } catch { return 0; }
};

const Planning = () => {
  const { scheduleSlots, pricingTiers, teachers, settings, loaded } = useData();

  const days = useMemo(() => {
    const byDay = {};
    (scheduleSlots || []).forEach((s) => {
      const k = s.day;
      if (!k) return;
      if (!byDay[k]) byDay[k] = { dayKey: k, day: DAY_LABELS[k] || k, slots: [] };
      byDay[k].slots.push(s);
    });
    return DAY_ORDER.filter((d) => byDay[d]).map((d) => {
      const day = byDay[d];
      day.slots.sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));
      return day;
    });
  }, [scheduleSlots]);

  if (!loaded) return <div className="min-h-[60vh] flex items-center justify-center text-stone-500">Chargement...</div>;
  const SITE = settings || {};

  return (
    <>
      <Seo title="Planning & Tarifs — L'Élan Danse" description={`Planning des cours de danse de la saison ${SITE.season} et tarifs annuels et trimestriels de L'Élan Danse au Perray-en-Yvelines.`} />

      <section className="py-20 px-6">
        <SectionTitle kicker={`Saison ${SITE.season || ""}`} title="Planning des cours" subtitle="Retrouvez ci-dessous l'organisation hebdomadaire de tous nos cours, du lundi au samedi." />

        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-center gap-4 mb-10">
          {teachers.map((t) => (
            <div key={t.id || t.slug} className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm">
              <span className={`w-4 h-4 rounded-full ${t.color || "bg-stone-400"}`} />
              <span className="font-serif-display text-stone-700">{t.name}</span>
            </div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {days.map((d, idx) => {
            const dayColors = ["bg-blush-light", "bg-sky-soft", "bg-cream-yellow", "bg-peach", "bg-mauve/40", "bg-rose-50"];
            return (
              <div key={d.dayKey} className={`${dayColors[idx % dayColors.length]} rounded-3xl p-6`} data-testid={`planning-day-${d.dayKey}`}>
                <h3 className="font-script text-4xl text-deep-rose text-center">{d.day}</h3>
                <div className="section-divider !mx-auto !w-16 my-3" />
                <ul className="space-y-3 mt-4">
                  {d.slots.map((s) => (
                    <li key={s.id} className={`bg-white rounded-xl p-3 shadow-sm border-l-4`} style={{ borderLeftColor: undefined }} data-slot-teacher={s.teacherName || ""}>
                      <div className="flex items-start gap-2">
                        <span className={`w-3 h-3 mt-1.5 rounded-full shrink-0 ${s.teacherColor || "bg-stone-400"}`} aria-hidden="true" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-serif-display">
                            {s.startTime} - {s.endTime}
                          </div>
                          <div className="font-serif-display text-lg leading-tight mt-0.5 text-stone-800">{s.title}</div>
                          <div className="flex items-center gap-1.5 text-xs text-stone-500 mt-1">
                            <MapPin size={12} /> {s.room}{s.teacherName ? ` · ${s.teacherName}` : ""}
                          </div>
                          {s.price > 0 && (
                            <div className="text-xs text-deep-rose font-medium mt-1">{Number(s.price).toFixed(0)} €/an</div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {SITE.planningNote && (
          <div className="max-w-4xl mx-auto mt-10 bg-blush-light rounded-2xl p-5 flex gap-3 items-start">
            <Info className="text-deep-rose shrink-0 mt-0.5" size={20} />
            <p className="text-stone-700 font-serif-display">{SITE.planningNote}</p>
          </div>
        )}
      </section>

      <section className="py-20 px-6 bg-blush-light/60">
        <SectionTitle
          kicker={settings?.pricingBlockKicker || "Tarifs par créneau"}
          title={settings?.pricingBlockTitle || "Nos tarifs"}
          subtitle={settings?.pricingBlockSubtitle || "Le tarif annuel de chaque cours est indiqué directement sur le créneau ci-dessus. Le total à régler est la somme des créneaux choisis."}
        />
        <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-sm p-6 sm:p-8">
          {(() => {
            const bullets = [
              settings?.pricingBullet1,
              settings?.pricingBullet2,
              settings?.pricingBullet3,
              settings?.pricingBullet4,
            ].filter((b) => (b || "").trim());
            if (settings?.pricingBlockText) {
              return <div className="text-stone-700 whitespace-pre-line leading-relaxed">{settings.pricingBlockText}</div>;
            }
            if (bullets.length > 0) {
              const renderBullet = (raw) => {
                // Parse **bold** markdown-lite
                const parts = raw.split(/(\*\*[^*]+\*\*)/g);
                return parts.map((p, i) =>
                  p.startsWith("**") && p.endsWith("**")
                    ? <strong key={i}>{p.slice(2, -2)}</strong>
                    : <React.Fragment key={i}>{p}</React.Fragment>
                );
              };
              return (
                <ul className="space-y-3 text-stone-700">
                  {bullets.map((b, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="text-deep-rose font-serif-display shrink-0">•</span>
                      <span className="whitespace-pre-line">{renderBullet(b)}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            return (
              <ul className="space-y-3 text-stone-700">
                <li className="flex gap-3"><span className="text-deep-rose font-serif-display shrink-0">•</span><span><strong>Remises famille</strong> : −{settings?.discount2nd ?? 10}% dès 2 créneaux · −{settings?.discount3rd ?? 15}% dès 3 créneaux, appliquée au créneau le moins cher de la famille.</span></li>
                <li className="flex gap-3"><span className="text-deep-rose font-serif-display shrink-0">•</span><span><strong>Cotisation association</strong> : {settings?.cotisationLocal ?? 15} € pour les résidents {settings?.cotisationPostalCode || "78610"}, {settings?.cotisationExt ?? 35} € hors commune.</span></li>
                <li className="flex gap-3"><span className="text-deep-rose font-serif-display shrink-0">•</span><span><strong>Licence FFD</strong> : {settings?.licenseFFD ?? 24} € par élève.</span></li>
              </ul>
            );
          })()}
        </div>
      </section>
    </>
  );
};

export default Planning;
